import React, { useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Target, Layers, ImageIcon, Plus, MoreHorizontal, ChevronRight,
  ChevronDown, Copy, Trash2, Lock, Rocket, Save, X, Sparkles,
} from 'lucide-react';
import { useFbWorkspace, EntityStatus } from '@/contexts/FbWorkspaceContext';
import { Button } from '@/components/Button';

// ─── Status dot ──────────────────────────────────────────────────────────────

function StatusDot({ status, locked }: { status: EntityStatus; locked?: boolean }) {
  const color: Record<EntityStatus, string> = {
    PUBLISHED: 'bg-success',
    SAVED:     'bg-warning',
    DRAFT:     'bg-border-strong',
  };
  return (
    <span className="flex items-center gap-0.5 shrink-0">
      {locked && <Lock size={10} className="text-text-muted" />}
      <span className={clsx('w-2 h-2 rounded-full', color[status])} />
    </span>
  );
}

// ─── Context-menu (... popup) ─────────────────────────────────────────────────

interface CtxMenuProps {
  items: { label: string; icon?: React.ReactNode; danger?: boolean; disabled?: boolean; onClick: () => void }[];
}

function CtxMenu({ items }: CtxMenuProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        className="flex h-6 w-6 items-center justify-center rounded-md text-text-muted hover:bg-surface-alt hover:text-text-primary opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <MoreHorizontal size={13} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-7 z-40 min-w-[140px] rounded-xl border border-border bg-surface-elevated shadow-elevated py-1">
            {items.map((item, i) => (
              <button
                key={i}
                type="button"
                disabled={item.disabled}
                onClick={e => { e.stopPropagation(); item.onClick(); setOpen(false); }}
                className={clsx(
                  'flex w-full items-center gap-2 px-3 py-2 text-xs font-medium transition-colors disabled:opacity-40',
                  item.danger
                    ? 'text-danger hover:bg-danger-soft'
                    : 'text-text-secondary hover:bg-surface-alt hover:text-text-primary',
                )}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Objective label map ──────────────────────────────────────────────────────

const OBJECTIVE_LABELS: Record<string, string> = {
  OUTCOME_AWARENESS:     'Awareness',
  OUTCOME_TRAFFIC:       'Traffic',
  OUTCOME_ENGAGEMENT:    'Engagement',
  OUTCOME_LEADS:         'Leads',
  OUTCOME_SALES:         'Sales',
  OUTCOME_APP_PROMOTION: 'App Promotions',
};

// ─── WorkspaceSidebar ─────────────────────────────────────────────────────────

interface Props {
  onActivate: () => void;
  isPublishing?: boolean;
}

export const WorkspaceSidebar: React.FC<Props> = ({ onActivate, isPublishing = false }) => {
  const {
    campaign, adSets,
    activeLevel, activeAdSetId, activeAdId,
    setActiveLevel,
    addAdSet, deleteAdSet, duplicateAdSet,
    addAd, deleteAd, duplicateAd,
    canPublish,
    saveDraft, discardDraft,
    aiGenerated, aiPublishedAt,
    platform, appPromotion,
  } = useFbWorkspace();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleAddAdSet = () => {
    const id = addAdSet();
    setExpanded(prev => new Set(prev).add(id));
  };

  const handleAddAd = (adSetId: string) => {
    addAd(adSetId);
    setExpanded(prev => new Set(prev).add(adSetId));
  };

  const totalAds = adSets.reduce((s, a) => s + a.ads.length, 0);

  return (
    <aside className="w-64 min-w-[256px] shrink-0 border-r border-border bg-surface flex flex-col h-full">
      {/* AI-generated banner */}
      {aiGenerated && (
        <div className={clsx(
          'px-3 py-2 flex items-start gap-2 text-xs border-b',
          aiPublishedAt
            ? 'bg-success-soft border-success/30 text-success'
            : 'bg-primary-soft border-primary/30 text-primary',
        )}>
          <Sparkles size={12} className="shrink-0 mt-0.5" />
          <span className="leading-snug font-medium">
            {aiPublishedAt
              ? 'AI-generated — review details, then Activate to go live.'
              : 'AI draft applied — fill in media & save to publish.'}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
            <Rocket size={15} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-text-primary leading-tight">
              {campaign.name || 'New Campaign'}
            </p>
            <p className="text-2xs text-text-muted">
              {OBJECTIVE_LABELS[campaign.objective] ?? 'Select objective'}
            </p>
          </div>
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {/* Campaign node */}
        <button
          type="button"
          onClick={() => setActiveLevel('campaign', activeAdSetId, null)}
          className={clsx(
            'group relative flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors',
            activeLevel === 'campaign'
              ? 'bg-primary-soft border border-primary/30'
              : 'hover:bg-surface-alt',
            activeLevel === 'campaign' && 'before:absolute before:left-0 before:inset-y-1.5 before:w-0.5 before:rounded-full before:bg-primary',
          )}
        >
          <Target size={14} className="text-primary shrink-0" />
          <span className="flex-1 truncate text-xs font-semibold text-text-primary">
            {campaign.name || 'Untitled Campaign'}
          </span>
          <StatusDot status={campaign.status} locked={campaign.isLocked} />
        </button>

        {/* Ad Set tree */}
        <div className="ml-3 border-l border-border pl-2 space-y-0.5">
          {adSets.map(as => {
            const isOpen = expanded.has(as.id) || as.ads.some(a => a.id === activeAdId);
            const isActive = activeLevel === 'adset' && activeAdSetId === as.id;

            return (
              <div key={as.id}>
                {/* Ad Set row */}
                <div
                  onClick={() => { setActiveLevel('adset', as.id, null); setExpanded(prev => new Set(prev).add(as.id)); }}
                  className={clsx(
                    'group relative flex items-center gap-1.5 rounded-md px-2 py-1.5 cursor-pointer transition-colors',
                    isActive
                      ? 'bg-info-soft border border-info/30'
                      : 'hover:bg-surface-alt',
                    isActive && 'before:absolute before:left-0 before:inset-y-1 before:w-0.5 before:rounded-full before:bg-info',
                  )}
                >
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); toggleExpand(as.id); }}
                    className="shrink-0 text-text-muted"
                  >
                    {as.ads.length > 0
                      ? (isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />)
                      : <span className="w-[11px] inline-block" />
                    }
                  </button>
                  <Layers size={12} className="text-info shrink-0" />
                  <span className="flex-1 truncate text-xs font-medium text-text-primary">{as.name}</span>
                  <StatusDot status={as.status} locked={as.isLocked} />
                  <CtxMenu
                    items={[
                      { label: 'Add Ad', icon: <Plus size={11} />, onClick: () => handleAddAd(as.id) },
                      { label: 'Duplicate', icon: <Copy size={11} />, onClick: () => duplicateAdSet(as.id) },
                      { label: 'Delete', icon: <Trash2 size={11} />, danger: true, disabled: as.isLocked, onClick: () => deleteAdSet(as.id) },
                    ]}
                  />
                </div>

                {/* Ads under this adset */}
                {isOpen && (
                  <div className="ml-4 border-l border-border pl-2 mt-0.5 space-y-0.5">
                    {as.ads.map(ad => {
                      const isAdActive = activeLevel === 'ad' && activeAdId === ad.id;
                      return (
                        <div
                          key={ad.id}
                          onClick={() => setActiveLevel('ad', as.id, ad.id)}
                          className={clsx(
                            'group relative flex items-center gap-1.5 rounded-md px-2 py-1.5 cursor-pointer transition-colors',
                            isAdActive
                              ? 'bg-success-soft border border-success/30'
                              : 'hover:bg-surface-alt',
                            isAdActive && 'before:absolute before:left-0 before:inset-y-1 before:w-0.5 before:rounded-full before:bg-success',
                          )}
                        >
                          <ImageIcon size={11} className="text-success shrink-0" />
                          <span className="flex-1 truncate text-xs text-text-primary">{ad.name}</span>
                          <StatusDot status={ad.status} locked={ad.isLocked} />
                          <CtxMenu
                            items={[
                              { label: 'Duplicate', icon: <Copy size={11} />, onClick: () => duplicateAd(as.id, ad.id) },
                              { label: 'Delete', icon: <Trash2 size={11} />, danger: true, disabled: ad.isLocked, onClick: () => deleteAd(as.id, ad.id) },
                            ]}
                          />
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => handleAddAd(as.id)}
                      className="flex items-center gap-1.5 px-2 py-1 w-full text-2xs text-text-muted hover:text-text-primary transition-colors"
                    >
                      <Plus size={10} /> Add Ad
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Ad Set */}
          <button
            type="button"
            onClick={handleAddAdSet}
            className="flex items-center gap-1.5 rounded-md px-2 py-1.5 w-full text-xs text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
          >
            <Plus size={12} /> Add Ad Set
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-border space-y-2">
        {/* Stats */}
        <div className="flex justify-between text-2xs text-text-muted px-1">
          <span>{adSets.length} ad set{adSets.length !== 1 ? 's' : ''}</span>
          <span>{totalAds} ad{totalAds !== 1 ? 's' : ''}</span>
        </div>

        {/* Save draft / Close */}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => {
              saveDraft();
              toast.success('Draft saved — it will be restored next time you open the workspace');
            }}
            className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-border bg-surface px-2 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors"
          >
            <Save size={12} /> Save draft
          </button>
          {/* ✕ closes the workspace (draft auto-saves). It used to DISCARD the
              draft silently with no navigation — which read as "the cross
              button does nothing" (user report, 2026-08-29). Discarding is on
              the campaign node's ⋯ menu semantics via starting fresh. */}
          <button
            type="button"
            onClick={() => {
              saveDraft();
              navigate(platform === 'instagram' ? '/instagram/campaigns' : '/facebook/campaigns');
            }}
            className="flex items-center justify-center gap-1 rounded-lg border border-border bg-surface px-2 py-1.5 text-xs font-medium text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
            title="Close workspace (your draft is saved)"
          >
            <X size={12} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Discard this draft and start over? Anything not yet saved to Meta will be lost.')) {
              discardDraft();
              toast.success('Draft discarded');
            }
          }}
          className="w-full text-center text-2xs text-text-muted transition-colors hover:text-danger"
        >
          Start over (discard draft)
        </button>

        {/* Activate */}
        {appPromotion ? <p className="rounded-lg bg-primary-soft p-3 text-xs text-text-secondary">App campaigns are saved paused. Review the campaign in Meta Ads Manager before enabling delivery.</p> : <Button
          variant="primary"
          size="sm"
          className="w-full"
          disabled={!canPublish || isPublishing}
          loading={isPublishing}
          onClick={onActivate}
          iconLeft={<Rocket size={13} />}
        >
          Activate Campaign
        </Button>}

        {!canPublish && !appPromotion && (
          <p className="text-center text-2xs text-text-muted px-1">
            {!campaign.isLocked
              ? 'Save campaign first'
              : !adSets.some(x => x.isLocked)
              ? 'Save at least one ad set'
              : 'Save at least one ad'}
          </p>
        )}
      </div>
    </aside>
  );
};
